// React Query hooks for the Courses feature. Mirrors the conventions in
// `lib/documents.ts`: `apiFetch` wrappers, `['courses', …]` query keys, and
// broad invalidation on the `['courses']` prefix after mutations.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  PublicCourse,
  PublicLesson,
  PublicLessonMaterial,
  PublicQuizForLearner,
  PublicCourseProgress,
  PublicLessonProgress,
  QuizResult,
  QuizSubmit,
  ExerciseSetConfig,
  CourseInput,
  CourseUpdate,
  LessonInput,
  LessonUpdate,
  LessonMaterialInput,
  LessonMaterialUpdate,
  QuizInput,
  QuizUpdate,
  LessonProgressInput,
} from '@vedaunion/shared';
import { apiFetch, ApiError } from './api';

export interface CourseListParams {
  status?: string;
  level?: string;
  limit?: number;
  all?: boolean;
  mine?: boolean;
  q?: string;
}

function buildQuery(params: CourseListParams): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === '') continue;
    sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

// -- Courses -------------------------------------------------------------------

export function useCourses(params: CourseListParams = {}) {
  return useQuery<{ courses: PublicCourse[] }>({
    queryKey: ['courses', 'list', params],
    queryFn: ({ signal }) => apiFetch<{ courses: PublicCourse[] }>(`/api/courses${buildQuery(params)}`, { signal }),
  });
}

export function useEnrolledCourses(enabled = true) {
  return useQuery<{ courses: PublicCourse[] }>({
    queryKey: ['courses', 'enrolled'],
    queryFn: ({ signal }) => apiFetch<{ courses: PublicCourse[] }>('/api/courses/enrolled', { signal }),
    enabled,
  });
}

export function useCourse(slug: string | undefined) {
  return useQuery<{ course: PublicCourse }>({
    queryKey: ['courses', 'detail', slug ?? ''],
    queryFn: ({ signal }) => apiFetch<{ course: PublicCourse }>(`/api/courses/${slug}`, { signal }),
    enabled: !!slug,
  });
}

export function useCourseProgress(slug: string | undefined, enabled = true) {
  return useQuery<{ progress: PublicCourseProgress }>({
    queryKey: ['courses', 'progress', slug ?? ''],
    queryFn: ({ signal }) => apiFetch<{ progress: PublicCourseProgress }>(`/api/courses/${slug}/progress`, { signal }),
    enabled: !!slug && enabled,
  });
}

export function useCreateCourse() {
  const qc = useQueryClient();
  return useMutation<{ course: PublicCourse }, ApiError, CourseInput>({
    mutationFn: (body) => apiFetch<{ course: PublicCourse }>('/api/courses', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['courses'] }),
  });
}

export function useUpdateCourse(id: number) {
  const qc = useQueryClient();
  return useMutation<{ course: PublicCourse }, ApiError, CourseUpdate>({
    mutationFn: (body) => apiFetch<{ course: PublicCourse }>(`/api/courses/${id}`, { method: 'PATCH', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['courses'] }),
  });
}

export function useDeleteCourse() {
  const qc = useQueryClient();
  return useMutation<{ ok: true; deleted: number }, ApiError, number>({
    mutationFn: (id) => apiFetch<{ ok: true; deleted: number }>(`/api/courses/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['courses'] }),
  });
}

export function useReorderCourseItems(courseId: number) {
  const qc = useQueryClient();
  return useMutation<{ course: PublicCourse }, ApiError, number[]>({
    mutationFn: (orderedIds) =>
      apiFetch<{ course: PublicCourse }>(`/api/courses/${courseId}/items/order`, {
        method: 'PATCH',
        body: { orderedIds },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['courses'] }),
  });
}

// -- Lessons -------------------------------------------------------------------

export function useLesson(lessonId: number | undefined) {
  return useQuery<{ lesson: PublicLesson }>({
    queryKey: ['courses', 'lesson', lessonId ?? 0],
    queryFn: ({ signal }) => apiFetch<{ lesson: PublicLesson }>(`/api/courses/lessons/${lessonId}`, { signal }),
    enabled: !!lessonId,
  });
}

export function useCreateLesson(courseId: number) {
  const qc = useQueryClient();
  return useMutation<{ lesson: PublicLesson }, ApiError, LessonInput>({
    mutationFn: (body) => apiFetch<{ lesson: PublicLesson }>(`/api/courses/${courseId}/lessons`, { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['courses'] }),
  });
}

export function useUpdateLesson(lessonId: number) {
  const qc = useQueryClient();
  return useMutation<{ lesson: PublicLesson }, ApiError, LessonUpdate>({
    mutationFn: (body) => apiFetch<{ lesson: PublicLesson }>(`/api/courses/lessons/${lessonId}`, { method: 'PATCH', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['courses'] }),
  });
}

export function useDeleteLesson() {
  const qc = useQueryClient();
  return useMutation<{ ok: true; deleted: number }, ApiError, number>({
    mutationFn: (lessonId) =>
      apiFetch<{ ok: true; deleted: number }>(`/api/courses/lessons/${lessonId}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['courses'] }),
  });
}

// -- Lesson materials ----------------------------------------------------------

export function useCreateLessonMaterial(lessonId: number) {
  const qc = useQueryClient();
  return useMutation<{ materials: PublicLessonMaterial[] }, ApiError, LessonMaterialInput>({
    mutationFn: (body) =>
      apiFetch<{ materials: PublicLessonMaterial[] }>(`/api/courses/lessons/${lessonId}/materials`, {
        method: 'POST',
        body,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['courses'] }),
  });
}

export function useUpdateLessonMaterial(lessonId: number) {
  const qc = useQueryClient();
  return useMutation<{ materials: PublicLessonMaterial[] }, ApiError, { id: number; body: LessonMaterialUpdate }>({
    mutationFn: ({ id, body }) =>
      apiFetch<{ materials: PublicLessonMaterial[] }>(`/api/courses/lessons/${lessonId}/materials/${id}`, {
        method: 'PATCH',
        body,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['courses'] }),
  });
}

export function useDeleteLessonMaterial(lessonId: number) {
  const qc = useQueryClient();
  return useMutation<{ materials: PublicLessonMaterial[] }, ApiError, number>({
    mutationFn: (id) =>
      apiFetch<{ materials: PublicLessonMaterial[] }>(`/api/courses/lessons/${lessonId}/materials/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['courses'] }),
  });
}

export function useReorderLessonMaterials(lessonId: number) {
  const qc = useQueryClient();
  return useMutation<{ materials: PublicLessonMaterial[] }, ApiError, number[]>({
    mutationFn: (orderedIds) =>
      apiFetch<{ materials: PublicLessonMaterial[] }>(`/api/courses/lessons/${lessonId}/materials/order`, {
        method: 'PATCH',
        body: { orderedIds },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['courses'] }),
  });
}

// -- Quizzes -------------------------------------------------------------------

export interface QuizForEdit {
  id: number;
  courseId: number;
  title: string;
  description: string | null;
  passThreshold: number;
  maxAttempts: number | null;
  content: ExerciseSetConfig;
}

export function useQuizForEdit(quizId: number | undefined) {
  return useQuery<{ quiz: QuizForEdit }>({
    queryKey: ['courses', 'quiz-edit', quizId ?? 0],
    queryFn: ({ signal }) => apiFetch<{ quiz: QuizForEdit }>(`/api/courses/quizzes/${quizId}/edit`, { signal }),
    enabled: !!quizId,
  });
}

export function useQuizForLearner(quizId: number | undefined) {
  return useQuery<{ quiz: PublicQuizForLearner }>({
    queryKey: ['courses', 'quiz-take', quizId ?? 0],
    queryFn: ({ signal }) => apiFetch<{ quiz: PublicQuizForLearner }>(`/api/courses/quizzes/${quizId}`, { signal }),
    enabled: !!quizId,
  });
}

export function useCreateQuiz(courseId: number) {
  const qc = useQueryClient();
  return useMutation<{ id: number; content: ExerciseSetConfig }, ApiError, QuizInput>({
    mutationFn: (body) =>
      apiFetch<{ id: number; content: ExerciseSetConfig }>(`/api/courses/${courseId}/quizzes`, { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['courses'] }),
  });
}

export function useUpdateQuiz(quizId: number) {
  const qc = useQueryClient();
  return useMutation<{ id: number; content: ExerciseSetConfig }, ApiError, QuizUpdate>({
    mutationFn: (body) =>
      apiFetch<{ id: number; content: ExerciseSetConfig }>(`/api/courses/quizzes/${quizId}`, { method: 'PATCH', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['courses'] }),
  });
}

export function useDeleteQuiz() {
  const qc = useQueryClient();
  return useMutation<{ ok: true; deleted: number }, ApiError, number>({
    mutationFn: (quizId) =>
      apiFetch<{ ok: true; deleted: number }>(`/api/courses/quizzes/${quizId}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['courses'] }),
  });
}

// -- Quiz attempts -------------------------------------------------------------

/** Submit a completed exercise run (client-computed score). */
export function useSubmitQuizAttempt(quizId: number) {
  const qc = useQueryClient();
  return useMutation<{ result: QuizResult }, ApiError, QuizSubmit>({
    mutationFn: (body) =>
      apiFetch<{ result: QuizResult }>(`/api/courses/quizzes/${quizId}/submit`, {
        method: 'POST',
        body,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['courses'] }),
  });
}

// -- Progress ------------------------------------------------------------------

export function useSaveLessonProgress(lessonId: number, courseSlug?: string) {
  const qc = useQueryClient();
  return useMutation<{ progress: PublicLessonProgress }, ApiError, LessonProgressInput>({
    mutationFn: (body) =>
      apiFetch<{ progress: PublicLessonProgress }>(`/api/courses/lessons/${lessonId}/progress`, {
        method: 'POST',
        body,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['courses', 'enrolled'] });
      if (courseSlug) qc.invalidateQueries({ queryKey: ['courses', 'progress', courseSlug] });
    },
  });
}
